import { parseAPIErrorResponse, parseStringtoNull } from "utils/utils";
import { useAuth } from "stores/auth/auth-context";
import { useToast } from "components/atoms/toast/toast-context";
import { IApiError } from "services/api/api.types";
import {
  UserType,
  CountryCode,
  Salutation,
  LoginClassType
} from "types/common/common.types";
import jwt_decode from "jwt-decode";
import { phoneRegExp } from "constants/constants";
import { registerStudent } from "services/api/profiles/profileService";
import { StudentFormSchema } from "../use-profile-form.types";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { IUseStudent } from "../use-profile-form.types";
import { useForm, SubmitHandler } from "react-hook-form";
import { object, mixed, string, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import trackEvent from "services/mixpanel/mixpanel";
import { webCheckoutEventNames } from "services/mixpanel/web-checkout-events";
import { createPayloadForWebCheckoutTrackingEvent } from "utils/mixpanel/web-checkout/web-checkout-mixpanel-utils";
import { constants } from "constants/constants";
import { IPackageAndTenureSummaryDetails } from "services/api/packages/packageTenureSummaryDetails.types";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { useGetCurrentPlatform } from "hooks/use-media-query/use-get-currrent-platform";
import { getSourceInfoEvent } from "utils/mixpanel/mixpanel-utils";
import * as Sentry from "@sentry/nextjs";

const FormSchema = object().shape({
  name: string().min(2, "Tulis minimal 2 karakter, ya.").required(),
  userType: mixed<UserType>().oneOf(Object.values(UserType)).required(),
  grade: string().required("Isi bagian ini juga, ya."),
  email: string()
    .lowercase()
    .email("Hmm... alamat email ini sepertinya salah.")
    .required(),
  parentName: string().min(2, "Tulis minimal 2 karakter, ya. ").required(),
  parentNumber: string()
    .required("Mohon lengkapi nomor HP kamu")
    .matches(phoneRegExp, "Masukin nomor HP yang aktif, ya.")
    .min(8, "Masukin minimal 8 angka, ya.")
    .max(13, "Hmm... nomornya kepanjangan, nih."),
  salutation: mixed<Salutation>().oneOf(Object.values(Salutation)).required(),
  classType: mixed<LoginClassType>()
    .oneOf(Object.values(LoginClassType))
    .required()
});

const useStudentForm = (
  userType: UserType,
  summaryPackageData: IPackageAndTenureSummaryDetails | undefined,
  defaultGrade?: string
): IUseStudent<StudentFormSchema> => {
  const router = useRouter();

  const { addToast } = useToast();

  const { user, signOutUser } = useAuth();
  const { userData, setUserData } = user;
  const [isLoading, setIsLoading] = useBoolean();

  const currentPlatform = useGetCurrentPlatform();

  const decodeJwtTokenFromRegisterToken = () => {
    try {
      if (userData && userData.register_token) {
        const number: { phoneNumber: string } = jwt_decode(
          userData.register_token
        );
        return number.phoneNumber;
      }
    } catch (err) {
      Sentry.captureException(err);
      signOutUser();
    }
  };

  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    watch
  } = useForm<InferType<typeof FormSchema>>({
    defaultValues: {
      userType: userType,
      salutation: Salutation.Bpk,
      grade: defaultGrade
    },
    mode: "onChange",
    delayError: constants.DELAY_ERROR,
    resolver: yupResolver(FormSchema)
  });

  const onSubmit: SubmitHandler<StudentFormSchema> = async ({
    name,
    grade,
    email,
    salutation,
    parentName,
    parentNumber
  }) => {
    const payload = createPayloadForWebCheckoutTrackingEvent({
      useAuthData: user,
      Class: true,
      level: true,
      packageType: summaryPackageData?.name || null,
      tenureValue: summaryPackageData?.tenure_details.tenure_text.text || null,
      userFlow: true
    });
    trackEvent({
      eventName: webCheckoutEventNames.continueClickedOnCreateProfile,
      payload
    });

    try {
      if (userData.register_token) {
        setIsLoading.on();
        const res = await registerStudent({
          payload: {
            name,
            grade: grade.split("_")[0],
            stream: parseStringtoNull(grade.split("_")[1]),
            class_type: grade.split("_")[2],
            email: email.toLowerCase(),
            phone_number: decodeJwtTokenFromRegisterToken() || "",
            country_code: CountryCode.Indonesia,
            notification_consent: user.isWhatsappConsentGiven,
            parent: {
              name: parentName,
              salutation,
              phone_number: parentNumber,
              country_code: CountryCode.Indonesia
            }
          },
          token: userData.register_token,
          platform: getSourceInfoEvent(currentPlatform)
        });
        const { message, data } = res;
        if (message === "OK") {
          const { parent_user, student_user } = data;
          setUserData({
            ...userData,
            parent_user: { ...userData.parent_user, ...parent_user },
            primary_user: student_user,
            register_token: null
          });

          router.push({
            pathname: "/paket/user/summary",
            query: { ...router.query, user_id: userData.primary_user?.id }
          });
          setIsLoading.off();
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);
      const { message } = error as IApiError;
      addToast({
        heading: message,
        variant: "error"
      });
      setIsLoading.off();
    }
  };

  useEffect(() => {
    watch();
  });

  useEffect(() => {
    const userType = getValues().userType as UserType;
    user.setUserData({ ...userData, userType });
  }, [getValues().userType]);

  return {
    control,
    handleSubmit,
    isValid,
    onSubmit,
    getPhoneNumberFromRegisterToken: decodeJwtTokenFromRegisterToken() || "",
    isLoading
  };
};

export { useStudentForm };
